import createCache from '@emotion/cache';
import { serializeStyles, type SerializedStyles } from '@emotion/serialize';
import type { SxInput, SxProps } from './sxProps';

const computeCache = createCache({ key: 'css' });
computeCache.compat = true;

function isPlainObject(value: unknown): value is Record<string, any> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function deepMerge(base: Record<string, any>, extra: Record<string, any>): Record<string, any> {
  const merged: Record<string, any> = { ...base };
  Object.entries(extra).forEach(([key, value]) => {
    if (isPlainObject(value) && isPlainObject(merged[key])) {
      merged[key] = deepMerge(merged[key], value);
      return;
    }
    merged[key] = value;
  });
  return merged;
}

function resolveObject(input: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {};
  Object.entries(input).forEach(([key, value]) => {
    if (value == null || value === false) return;
    result[key] = isPlainObject(value) ? resolveObject(value) : value;
  });
  return result;
}

function resolveStyles(styles: unknown): Record<string, any> {
  if (styles == null || styles === false) return {};
  if (Array.isArray(styles)) {
    return styles.reduce<Record<string, any>>(
      (merged, item) => deepMerge(merged, resolveStyles(item)),
      {},
    );
  }
  if (!isPlainObject(styles)) return {};
  return resolveObject(styles);
}

function insertClass(serialized: SerializedStyles) {
  const className = `${computeCache.key}-${serialized.name}`;
  if (computeCache.inserted[serialized.name] === undefined) {
    let current: SerializedStyles | undefined = serialized;
    while (current) {
      computeCache.insert(
        current === serialized ? `.${className}` : '',
        current,
        computeCache.sheet,
        true,
      );
      current = current.next;
    }
  }
  const rule = computeCache.inserted[serialized.name];
  return { className, css: typeof rule === 'string' ? rule : '' };
}

export function useClassName(sx?: SxInput, base?: SxProps, className?: string) {
  const generated = insertClass(serializeStyles([resolveStyles(base ? [base, sx] : sx)]));
  return {
    className: className ? `${className} ${generated.className}` : generated.className,
    css: generated.css,
    styleId: generated.className,
  };
}
